import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/utils/api";
import { BarChart2, Database, TrendingUp, AlertCircle, Users, Truck, CheckCircle, Clock, DollarSign, Activity, RefreshCw, Zap, Shield } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { PointsWidget, PointsLeaderboard } from "@/components/gamification";

const LIFECYCLE_COLORS = {
  pending: "#94A3B8", ci_done: "#3B82F6", dtm_done: "#8B5CF6",
  dtc_done: "#EAB308", mi_done: "#F97316", qc_passed: "#16A34A",
  wcc_gen: "#059669", invoiced: "#0D9488", paid: "#065F46", rework: "#DC2626"
};

const StatusBadge = ({ status }) => {
  const colors = {
    pending: "bg-slate-100 text-slate-600", ci_done: "bg-blue-100 text-blue-700",
    dtm_done: "bg-purple-100 text-purple-700", dtc_done: "bg-yellow-100 text-yellow-700",
    mi_done: "bg-orange-100 text-orange-700", qc_passed: "bg-green-100 text-green-700",
    paid: "bg-emerald-100 text-emerald-700", rework: "bg-red-100 text-red-700",
    on_hold: "bg-red-100 text-red-700"
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${colors[status] || "bg-slate-100 text-slate-600"}`}>
      {status?.replace("_", " ")}
    </span>
  );
};

const MetricCard = ({ icon: Icon, label, value, sub, color = "orange", change }) => (
  <div className="metric-card-primary hover:-translate-y-0.5 hover:shadow-md transition-all duration-200">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</p>
        <p className="text-3xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>{value}</p>
        {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center bg-orange-50`}>
        <Icon size={20} className="text-orange-500" />
      </div>
    </div>
    {change !== undefined && (
      <div className={`mt-2 text-xs font-medium ${change >= 0 ? "text-green-600" : "text-red-500"}`}>
        {change >= 0 ? "+" : ""}{change}% vs yesterday
      </div>
    )}
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [funnel, setFunnel] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sumRes, funnelRes] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/dashboard/funnel")
      ]);
      setSummary(sumRes.data);
      setFunnel(funnelRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const dt = summary?.dt_stats || {};
  const holds = summary?.holds_by_responsibility || {};
  const holdsData = Object.entries(holds).map(([k, v]) => ({ name: k.toUpperCase(), value: v }));
  const HOLD_COLORS = ["#DC2626", "#F97316", "#EAB308", "#94A3B8"];

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fade-up" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>
            {user?.circle_name ? `${user.circle_name} Dashboard` : "Command Dashboard"}
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 border-2 border-slate-200 rounded-sm text-sm font-semibold text-slate-600 hover:border-orange-400 hover:text-orange-600 transition-colors"
          data-testid="refresh-btn"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Points Widget - Show for field roles */}
      {["technician", "helper", "supervisor", "surveyor"].includes(user?.role?.toLowerCase()) && (
        <PointsWidget className="mb-2" />
      )}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="metric-card animate-pulse">
              <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-slate-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* KPI Grid */}
          {user?.role === "supervisor" ? (
            /* Supervisor: Single row - Total, CI, DTM, DTC, MI, QC3, On Hold, Rework */
            <div className="grid grid-cols-4 md:grid-cols-8 gap-3" data-testid="supervisor-kpi-grid">
              <MetricCard icon={Database} label="Total DTs" value={dt.total?.toLocaleString() || "0"} />
              <MetricCard icon={BarChart2} label="CI Done" value={dt.ci_done?.toLocaleString() || "0"} />
              <MetricCard icon={Activity} label="DTM Done" value={dt.dtm_done?.toLocaleString() || "0"} />
              <MetricCard icon={Zap} label="DTC Done" value={dt.dtc_done?.toLocaleString() || "0"} />
              <MetricCard icon={CheckCircle} label="MI Done" value={dt.mi_done?.toLocaleString() || "0"} />
              <MetricCard icon={Shield} label="QC Passed" value={dt.qc_passed?.toLocaleString() || "0"} />
              <MetricCard icon={AlertCircle} label="On Hold" value={dt.on_hold?.toLocaleString() || "0"} />
              <MetricCard icon={Clock} label="Rework" value={summary?.rework_pending?.toLocaleString() || "0"} />
            </div>
          ) : (
            /* Admin/CI/PH: 2 rows - includes Paid */
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard icon={Database} label="Total DTs" value={dt.total?.toLocaleString() || "0"} sub="in scope" />
              <MetricCard icon={CheckCircle} label="MI Done" value={dt.mi_done?.toLocaleString() || "0"} sub={`${dt.saturation_pct || 0}% saturation`} />
              <MetricCard icon={TrendingUp} label="QC Passed" value={dt.qc_passed?.toLocaleString() || "0"} sub="ready to bill" />
              <MetricCard icon={DollarSign} label="Paid" value={dt.paid?.toLocaleString() || "0"} sub="DTs invoiced & paid" />
              <MetricCard icon={BarChart2} label="CI Done" value={dt.ci_done?.toLocaleString() || "0"} sub="surveys complete" />
              <MetricCard icon={Activity} label="DTM Done" value={dt.dtm_done?.toLocaleString() || "0"} sub="meters mounted" />
              <MetricCard icon={AlertCircle} label="On Hold" value={dt.on_hold?.toLocaleString() || "0"} sub="blocked DTs" />
              <MetricCard icon={Clock} label="Rework" value={summary?.rework_pending?.toLocaleString() || "0"} sub="pending rework" />
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Funnel Chart */}
            <div className="bg-white border border-slate-200 rounded-sm p-4 md:col-span-2">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">DT Lifecycle Funnel</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={funnel} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#94a3b8" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip
                    contentStyle={{ border: "1px solid #e2e8f0", borderRadius: "2px", fontSize: "12px" }}
                    formatter={(val, name, props) => [val, props.payload.label]}
                  />
                  <Bar dataKey="count" radius={[2, 2, 0, 0]}>
                    {funnel.map((entry) => (
                      <Cell key={entry.stage} fill={LIFECYCLE_COLORS[entry.stage] || "#94A3B8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Hold Breakdown */}
            <div className="bg-white border border-slate-200 rounded-sm p-4">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Holds by Responsibility</h3>
              {holdsData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={holdsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={10}>
                      {holdsData.map((_, i) => (
                        <Cell key={i} fill={HOLD_COLORS[i % HOLD_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: "2px", fontSize: "12px" }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                  <div className="text-center">
                    <CheckCircle size={32} className="mx-auto mb-2 text-green-400" />
                    No active holds
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Progress Bars */}
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide mb-4">Lifecycle Progress</h3>
            <div className="space-y-3">
              {[
                { label: "CI (Survey)", value: dt.ci_done, total: dt.total, color: "bg-blue-500" },
                { label: "DTM (Mounted)", value: dt.dtm_done, total: dt.total, color: "bg-purple-500" },
                { label: "MI (Commissioned)", value: dt.mi_done, total: dt.total, color: "bg-orange-500" },
                { label: "QC Passed", value: dt.qc_passed, total: dt.total, color: "bg-green-500" },
              ].map(({ label, value, total, color }) => {
                const pct = total > 0 ? Math.round((value / total) * 100) : 0;
                return (
                  <div key={label}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-semibold text-slate-600">{label}</span>
                      <span className="text-slate-500">{value?.toLocaleString() || 0} / {total?.toLocaleString() || 0} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "View DT Master", to: "/dt-master", color: "text-blue-600 hover:bg-blue-50 border-blue-200" },
              { label: "Manage Holds", to: "/holds", color: "text-red-600 hover:bg-red-50 border-red-200" },
              { label: "QC / Rework", to: "/qc", color: "text-green-600 hover:bg-green-50 border-green-200" },
              { label: "Command Center", to: "/command-center", color: "text-orange-600 hover:bg-orange-50 border-orange-200" },
            ].map(action => (
              <button
                key={action.to}
                onClick={() => navigate(action.to)}
                className={`p-3 border rounded-sm text-sm font-semibold transition-colors ${action.color}`}
                data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {action.label}
              </button>
            ))}
          </div>

          {/* Points Leaderboard - Show for supervisors and admins */}
          {["supervisor", "admin", "super_admin", "ci", "circle_incharge", "package_head"].includes(user?.role?.toLowerCase()) && (
            <PointsLeaderboard className="mt-6" />
          )}
        </>
      )}
    </div>
  );
}
